import Moment from 'moment';
import { ProductTypes, isEndorsableProductType, isPrimeX } from '../../types';
import { getBoolValueText } from '../../utils/appUtils';
import { getCoverages } from '../_global/endorsements/endorsement.utils';
import { toUniversalUtcDate } from '../../utils/date.utils';
import CANCELLATION_REASONS from '../_statics/cancellationreasons.statics';

export const formatPolicies = ({ data }: any) => {
  return data.map((policy: any) => formatPolicy(policy));
};

export function getCancellationReasonLabel(policy: any) {
  let cancellationReason: any;
  if (isPrimeX(policy?.product)) {
    cancellationReason = policy?.cancelReason;
  } else {
    cancellationReason = policy?.cancellationReason;
  }

  const label = CANCELLATION_REASONS.find(
    (option) => option.value.toLowerCase() == cancellationReason?.toLowerCase()
  )?.label;

  return label;
}

export const formatPolicy = (policy: any) => {

  return {
    ...policy,
    uiBinderExpiresOn: toUniversalUtcDate(policy.binderExpiresOn, {
      placeholder: null,
    }),
    uiEffectiveDate: toUniversalUtcDate(policy.effectiveDate, {
      placeholder: null,
    }),
    uiPolicyExpiresOn: toUniversalUtcDate(policy.expiresOn, {
      placeholder: null,
    }),
    uiCustomerFullName:
      policy.customerFirstName && policy.customerLastName
        ? `${policy.customerFirstName} ${policy.customerLastName}`
        : null,
    uiAgentFullName:
      policy.agentFirstName && policy.agentLastName
        ? `${policy.agentFirstName} ${policy.agentLastName}`
        : null,
    uiAgentInitials:
      policy.agentFirstName && policy.agentLastName
        ? `${policy.agentFirstName[0]}${policy.agentLastName[0]}`
        : null,
    uiTerm:
      policy.effectiveDate && policy.expiresOn
        ? Moment.utc(policy.expiresOn).diff(
            Moment.utc(policy.effectiveDate),
            'days'
          )
        : null,
    uiIsEndorsement: getBoolValueText(policy.isEndorsement),
    uiAddress: formatAddress({
      address1: policy.address1,
      city: policy.city,
      state: policy.state,
      zipCode: policy.zipCode,
    }),
    uiCancellationReason: getCancellationReasonLabel(policy),
  };
};

// used to display base policy + endorsements
export const formatForLatestPolicy = (policy: any, product: any) => {
  if (!isEndorsableProductType(product)) {
    return formatPolicy(policy);
  }

  const { originalPolicy = {}, latestPolicy = {} } = policy;

  const hasP250InitialRequestData = Boolean(latestPolicy?.initialRequestData);

  let latestCoverages = {};
  let brokerOfRecord = {};
  let isBOR = false;
  if (product === ProductTypes.p100) {
    latestCoverages = getCoverages(latestPolicy, product);
    brokerOfRecord = originalPolicy?.brokerOfRecord ?? null;
    isBOR = originalPolicy?.isBOR ?? false;
  } else if (product === ProductTypes.p250 && hasP250InitialRequestData) {
    latestCoverages = latestPolicy?.initialRequestData?.coverages;
    brokerOfRecord = latestPolicy?.brokerOfRecord ?? null;
    isBOR = latestPolicy?.isBOR ?? false;
  } else if (isPrimeX(product)) {
    latestCoverages = latestPolicy?.coverages;
  }

  return {
    ...originalPolicy,
    latestCoverages,
    isBOR,
    brokerOfRecord,
    companyName: originalPolicy?.firmographicData?.name,
    uiEffectiveDate: toUniversalUtcDate(originalPolicy.effectiveDate, {
      placeholder: null,
    }),
    expiresOn: toUniversalUtcDate(latestPolicy.endDate, {
      placeholder: null,
    }),
    uiCustomerFullName:
      originalPolicy.customerFirstName && originalPolicy.customerLastName
        ? `${originalPolicy.customerFirstName} ${originalPolicy.customerLastName}`
        : null,
    uiAgentFullName:
      originalPolicy.agentFirstName && originalPolicy.agentLastName
        ? `${originalPolicy.agentFirstName} ${originalPolicy.agentLastName}`
        : null,
    uiAgentInitials:
      originalPolicy.agentFirstName && originalPolicy.agentLastName
        ? `${originalPolicy.agentFirstName[0]}${originalPolicy.agentLastName[0]}`
        : null,
    limit: latestPolicy.limit,
    uiTerm:
      originalPolicy.effectiveDate && latestPolicy.endDate
        ? Moment.utc(latestPolicy.endDate).diff(
            Moment.utc(originalPolicy.effectiveDate),
            'days'
          )
        : null,
    deductible: latestPolicy.deductible,
    uiIsEndorsement: getBoolValueText(originalPolicy.isEndorsement),
    uiAddress: formatAddress({
      address1: originalPolicy.address1,
      city: originalPolicy.city,
      state: originalPolicy.state,
      zipCode: originalPolicy.zipCode,
    }),
    uiCancellationReason: originalPolicy?.cancellationReason
      ?.split('_')
      .join(' '),
  };
};

function formatAddress({ address1, city, state, zipCode }: any) {
  let addr = '';
  if (address1) {
    addr += address1;
  }
  if (city) {
    addr += `, ${city}`;
  }
  if (state) {
    addr += `, ${state}`;
  }
  if (zipCode) {
    addr += ` ${zipCode}`;
  }

  return addr;
}
